import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { onSnapshot, orderBy, query, where } from "firebase/firestore";
import { useAuth } from "../auth";
import { dishTitle, useI18n } from "../lib/i18n";
import { ProgressCell, StageLegend } from "../components/Progress";
import { type DishDoc } from "../lib/types";

type Row = DishDoc & { id: string };

export function Dishes() {
  const { user, profile, paths } = useAuth();
  const { t, locale, lang } = useI18n();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isStaff = profile?.role === "admin" || profile?.role === "chef";

  useEffect(() => {
    if (!user) return;
    // 決定1: 作業者は担当品目のみ。スタッフは全件。
    // ルールでも同じ制約を掛けているので、ここでのクエリはUXのため
    // (ルールに合わないクエリは permission-denied で落ちる)。
    if (!paths) return;
    const base = paths.dishes();
    const q = isStaff
      ? query(base, orderBy("updatedAt", "desc"))
      : query(base, where("assigneeUids", "array-contains", user.uid), orderBy("updatedAt", "desc"));

    return onSnapshot(
      q,
      // 削除済み(論理削除)は一覧に出さない。データは残っている(functions/delete-dish.mjs)
      (snap) => setRows(snap.docs.map((d) => ({ id: d.id, ...(d.data() as DishDoc) })).filter((d) => !d.deletedAt)),
      (e) => setError(e.message),
    );
  }, [user, isStaff, paths]);

  return (
    <div className="page">
      <header className="page-head">
        <h1>{t("品目")}</h1>
        {isStaff && (
          <Link className="button" to="/dishes/new">
            {t("+ 新しい品目を追加")}
          </Link>
        )}
      </header>

      <StageLegend />

      {error && <p className="error">{t("読み込みに失敗しました:")} {error}</p>}
      {rows === null && !error && <p className="muted">{t("読み込み中…")}</p>}
      {rows?.length === 0 && (
        <p className="muted">
          {isStaff
            ? t("まだ品目がありません。「新しい品目を追加」から動画とレシピメモをアップロードしてください。")
            : t("担当に割り当てられた品目がありません。管理者にご確認ください。")}
        </p>
      )}

      {rows && rows.length > 0 && (
        <table className="table">
          <thead>
            <tr>
              <th>{t("品目")}</th>
              <th>{t("進捗")}</th>
              <th>{t("版")}</th>
              <th>{t("更新")}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((d) => (
              <tr key={d.id}>
                <td data-label={t("品目")}>
                  <Link to={`/dishes/${d.id}`}>{dishTitle(d, lang)}</Link>
                  {/* 読み(カナ)は日本語話者向け。中国語表示では出さない */}
                  {lang === "ja" && d.nameJa && <span className="muted"> / {d.nameJa}</span>}
                </td>
                <td className="col-stage" data-label={t("進捗")}>
                  <ProgressCell dish={d} />
                </td>
                {/* 承認済みでも版数が入っていない品目がある（版数の取り違えで
                    0 のまま書かれた時期のもの）。「第0版」とは出さない */}
                <td data-label={t("版")}>{d.isGolden && d.version > 0 ? t("第{n}版", { n: d.version }) : "—"}</td>
                <td className="muted" data-label={t("更新")}>
                  {d.updatedAt ? d.updatedAt.toDate().toLocaleString(locale) : "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
